/**
 * Signal Freshness & Verification Engine for BizNearYou
 * Provides transparent timestamps, confidence levels, status classifications,
 * decay multipliers, and uncertainty disclaimers for all local business growth and hiring signals.
 *
 * Truthfully distinguishes:
 * 1. source_posted_at (Public source post date)
 * 2. source_published_at (Public source publication date)
 * 3. source_updated_at (Public source modification/update date)
 * 4. first_seen_at (When BizNearYou first discovered the evidence during search)
 * 5. last_checked_at (When BizNearYou last checked/audited the source URL)
 * 6. signal_calculated_at (When BizNearYou calculated the signal score during search)
 */

import { normalizeTags } from "./tagUtils";

export type SignalStatus = 
  | "Confirmed Signal" 
  | "Strong Growth Signal" 
  | "Possible Hiring Need" 
  | "Unconfirmed Activity" 
  | "No Longer Observed" 
  | "Expired Signal"
  | "fresh"
  | "recent"
  | "aging"
  | "stale"
  | "expired"
  | "source_date_unspecified"
  | "unknown"
  | (string & {});

export type EvidenceDecayProfile = "fast" | "medium" | "slow";

export interface SignalFreshnessRecord {
  businessId?: string;
  businessName?: string;
  signalType?: string;
  signal_type?: string;
  signal_level?: string;
  signal_score?: number;
  signal_confidence?: number;
  signal_source_url?: string;
  signal_source_type?: string;

  // 1. Public Source Dates
  source_posted_at?: string | null;
  source_published_at?: string | null;
  source_updated_at?: string | null;

  // 2. BizNearYou Internal Activity Timestamps
  first_seen_at: string;
  last_seen_at: string;
  last_checked_at: string;
  signal_calculated_at: string;

  // Legacy & Alias Compatibility Fields
  firstSeenAt?: string;
  lastSeenAt?: string;
  lastCheckedAt?: string;
  last_updated_at?: string | null;
  signal_source_updated_at?: string | null;
  signal_first_seen_at?: string;
  signal_last_seen_at?: string;
  signal_last_checked_at?: string;
  detected_at?: string;

  has_source_date: boolean;
  source?: string;
  confidence?: number;
  confidenceScore: number;
  status: SignalStatus;
  evidenceSummary?: string;
  disclaimer?: string;
  signal_freshness_score?: number;
  signal_decay_multiplier?: number;
  decay_profile?: EvidenceDecayProfile;
}

export const STANDARD_SIGNAL_DISCLAIMER = 
  "A hiring signal indicates recent public commercial activity, review momentum, or location expansion. It does not guarantee an active job vacancy. Verify current staffing needs when contacting the business owner.";

/**
 * Returns evidence decay profile based on evidence type or signal category.
 */
export function getDecayProfileForEvidence(evidenceType?: string, signalCategory?: string): EvidenceDecayProfile {
  const lower = ((evidenceType || "") + " " + (signalCategory || "")).toLowerCase();
  
  if (
    lower.includes("now hiring") ||
    lower.includes("job listing") ||
    lower.includes("post") ||
    lower.includes("urgent") ||
    lower.includes("social") ||
    lower.includes("opening")
  ) {
    return "fast";
  }

  if (
    lower.includes("website") ||
    lower.includes("seo") ||
    lower.includes("branding") ||
    lower.includes("location") ||
    lower.includes("contact") ||
    lower.includes("phone") ||
    lower.includes("category") ||
    lower.includes("popularity") ||
    lower.includes("review")
  ) {
    return "slow";
  }

  return "medium";
}

/**
 * Standard signal decay multiplier calculation.
 */
export function getSignalDecayMultiplier(ageInDays: number | null, profile: EvidenceDecayProfile = "medium"): number {
  if (ageInDays === null || isNaN(ageInDays) || ageInDays < 0) return 1.0;

  if (profile === "fast") {
    if (ageInDays <= 7) return 1.0;
    if (ageInDays <= 14) return 0.9;
    if (ageInDays <= 30) return 0.7;
    if (ageInDays <= 60) return 0.4;
    if (ageInDays <= 90) return 0.2;
    return 0.05;
  }

  if (profile === "slow") {
    if (ageInDays <= 30) return 1.0;
    if (ageInDays <= 90) return 0.95;
    if (ageInDays <= 180) return 0.85;
    if (ageInDays <= 365) return 0.7;
    return 0.5;
  }

  // Default / Medium profile:
  if (ageInDays <= 14) return 1.0;
  if (ageInDays <= 30) return 0.9;
  if (ageInDays <= 60) return 0.75;
  if (ageInDays <= 90) return 0.6;
  if (ageInDays <= 180) return 0.4;
  if (ageInDays <= 365) return 0.2;
  return 0.05;
}

/**
 * Freshness status classification based on age in days.
 */
export function getFreshnessStatus(
  ageInDays: number | null,
  profile: EvidenceDecayProfile = "medium"
): "fresh" | "recent" | "aging" | "stale" | "expired" | "source_date_unspecified" {
  if (ageInDays === null || isNaN(ageInDays) || ageInDays < 0) return "source_date_unspecified";

  if (profile === "fast") {
    if (ageInDays <= 14) return "fresh";
    if (ageInDays <= 30) return "recent";
    if (ageInDays <= 60) return "aging";
    if (ageInDays <= 90) return "stale";
    return "expired";
  }

  if (profile === "slow") {
    if (ageInDays <= 60) return "fresh";
    if (ageInDays <= 120) return "recent";
    if (ageInDays <= 180) return "aging";
    if (ageInDays <= 365) return "stale";
    return "expired";
  }

  // Medium profile:
  if (ageInDays <= 30) return "fresh";
  if (ageInDays <= 60) return "recent";
  if (ageInDays <= 90) return "aging";
  if (ageInDays <= 180) return "stale";
  return "expired";
}

/**
 * Extracts and normalizes the 6 distinct timestamps for a business signal.
 * Never creates fake dates or assigns current search time to public source update dates.
 */
export function getTruthfulSignalDates(
  business: any,
  rawSourceUpdated?: string | null,
  rawSourcePublished?: string | null,
  rawSourcePosted?: string | null
) {
  const now = new Date().toISOString();

  const parseValidISO = (val: any): string | null => {
    if (!val || val === "Unknown" || val === "null" || val === "undefined") return null;
    const ms = new Date(val).getTime();
    return isNaN(ms) ? null : new Date(ms).toISOString();
  };

  const sourcePosted = parseValidISO(rawSourcePosted) || parseValidISO(business?.source_posted_at) || parseValidISO(business?.last_hiring_update_date) || null;
  const sourcePublished = parseValidISO(rawSourcePublished) || parseValidISO(business?.source_published_at) || parseValidISO(business?.last_expansion_update_date) || null;
  const sourceUpdated = parseValidISO(rawSourceUpdated) || parseValidISO(business?.source_updated_at) || parseValidISO(business?.signal_source_updated_at) || null;

  const firstSeen = parseValidISO(business?.first_seen_at) || parseValidISO(business?.firstSeenAt) || now;
  const lastChecked = parseValidISO(business?.last_checked_at) || parseValidISO(business?.lastCheckedAt) || now;
  const signalCalculated = parseValidISO(business?.signal_calculated_at) || now;

  return {
    source_posted_at: sourcePosted,
    source_published_at: sourcePublished,
    source_updated_at: sourceUpdated,
    first_seen_at: firstSeen,
    last_seen_at: lastChecked,
    last_checked_at: lastChecked,
    signal_calculated_at: signalCalculated,
    has_source_date: Boolean(sourcePosted || sourcePublished || sourceUpdated)
  };
}

/**
 * Evaluates signal status and confidence decay strictly based on public source timestamps.
 */
export function evaluateSignalFreshness(
  businessId: any,
  businessName?: any,
  signalType?: any,
  rawFirstSeen?: any,
  rawLastSeen?: any,
  rawLastChecked?: any,
  rawConfidence = 80,
  rawSourceUpdated?: any,
  profile: EvidenceDecayProfile = "medium"
): SignalFreshnessRecord {
  const idStr = typeof businessId === "object" ? (businessId.id || businessId.name || "biz") : String(businessId || "biz");
  const nameStr = typeof businessId === "object" ? (businessId.name || "Business") : String(businessName || "Business");
  const sigStr = String(signalType || "Commercial Activity");

  const dates = getTruthfulSignalDates(
    typeof businessId === "object" ? businessId : null,
    rawSourceUpdated,
    null,
    null
  );

  const effectiveSourceDate = dates.source_updated_at || dates.source_published_at || dates.source_posted_at;
  const decay = calculateSignalDecay(effectiveSourceDate, Number(rawConfidence || 80), profile);

  let status: SignalStatus = "Strong Growth Signal";
  if (!decay.hasSourceDate) {
    status = "Confirmed Signal";
  } else if (decay.status === "fresh") {
    status = decay.confidence >= 80 ? "Confirmed Signal" : "Strong Growth Signal";
  } else if (decay.status === "recent") {
    status = "Possible Hiring Need";
  } else if (decay.status === "aging") {
    status = "Unconfirmed Activity";
  } else if (decay.status === "stale") {
    status = "No Longer Observed";
  } else {
    status = "Expired Signal";
  }

  const formattedCheckedDate = dates.last_checked_at.split("T")[0];

  return {
    businessId: idStr,
    businessName: nameStr,
    signalType: sigStr,
    signal_type: sigStr,

    source_posted_at: dates.source_posted_at,
    source_published_at: dates.source_published_at,
    source_updated_at: dates.source_updated_at,

    first_seen_at: dates.first_seen_at,
    last_seen_at: dates.last_checked_at,
    last_checked_at: dates.last_checked_at,
    signal_calculated_at: dates.signal_calculated_at,

    firstSeenAt: dates.first_seen_at,
    lastSeenAt: dates.last_checked_at,
    lastCheckedAt: dates.last_checked_at,

    last_updated_at: dates.source_updated_at,
    signal_source_updated_at: dates.source_updated_at,
    signal_first_seen_at: dates.first_seen_at,
    signal_last_seen_at: dates.last_checked_at,
    signal_last_checked_at: dates.last_checked_at,
    detected_at: dates.first_seen_at,

    has_source_date: dates.has_source_date,
    source: "Public Directories & Evidence Engine",
    confidence: decay.confidence,
    confidenceScore: decay.confidence,
    status,
    evidenceSummary: dates.has_source_date
      ? `Public ${sigStr.toLowerCase()} posted/updated on ${effectiveSourceDate?.split("T")[0]}. Verified active by BizNearYou on ${formattedCheckedDate}.`
      : `Public ${sigStr.toLowerCase()} active. Verified live by BizNearYou on ${formattedCheckedDate}. (Public source publication date not specified).`,
    disclaimer: STANDARD_SIGNAL_DISCLAIMER,
    signal_freshness_score: Math.round(decay.freshnessMultiplier * 100),
    signal_decay_multiplier: decay.freshnessMultiplier,
    decay_profile: profile
  };
}

/**
 * Calculates signal decay based on public source publication/modification date.
 * If source date is null or unspecified, returns status 'source_date_unspecified' without fake penalties.
 */
export function calculateSignalDecay(
  sourceDateISO?: string | null,
  baseConfidence: number = 80,
  profile: EvidenceDecayProfile = "medium"
): {
  confidence: number;
  daysOld: number | null;
  ageDays: number | null;
  freshnessMultiplier: number;
  status: "fresh" | "recent" | "aging" | "stale" | "expired" | "source_date_unspecified";
  hasSourceDate: boolean;
} {
  if (!sourceDateISO || sourceDateISO === "Unknown" || sourceDateISO === "null" || sourceDateISO === "undefined") {
    return {
      confidence: baseConfidence,
      daysOld: null,
      ageDays: null,
      freshnessMultiplier: 1.0,
      status: "source_date_unspecified",
      hasSourceDate: false
    };
  }

  const sourceTime = new Date(sourceDateISO).getTime();
  if (isNaN(sourceTime)) {
    return {
      confidence: baseConfidence,
      daysOld: null,
      ageDays: null,
      freshnessMultiplier: 1.0,
      status: "source_date_unspecified",
      hasSourceDate: false
    };
  }

  const now = Date.now();
  const daysOld = Math.max(0, Math.floor((now - sourceTime) / (1000 * 60 * 60 * 24)));
  const freshnessMultiplier = getSignalDecayMultiplier(daysOld, profile);
  const confidence = Math.max(10, Math.min(100, Math.round(Number(baseConfidence || 80) * freshnessMultiplier)));
  const status = getFreshnessStatus(daysOld, profile);

  return { confidence, daysOld, ageDays: daysOld, freshnessMultiplier, status, hasSourceDate: true };
}

export function getOrCreateSignalFreshness(
  businessId: any,
  businessName?: any,
  signalType?: any,
  ...rest: any[]
): SignalFreshnessRecord {
  return evaluateSignalFreshness(
    businessId,
    businessName,
    signalType,
    rest[0],
    rest[1],
    rest[2],
    rest[3],
    rest[4],
    rest[5]
  );
}

export function getSignalFreshnessMultiplier(target?: any, _signalKey?: string, ..._rest: any[]): number {
  if (!target) return 1.0;
  if (typeof target === "string") {
    const { freshnessMultiplier } = calculateSignalDecay(target);
    return freshnessMultiplier;
  }
  const dateStr = target?.source_updated_at || target?.source_published_at || target?.source_posted_at;
  const { freshnessMultiplier } = calculateSignalDecay(dateStr);
  return freshnessMultiplier;
}

export function filterActiveAndFreshTags(firstArg: any, secondArg?: any, cutoffArg?: number): string[] {
  let rawTags: any;
  let cutoff = 20;

  if (Array.isArray(firstArg) || typeof firstArg === "string" || firstArg instanceof Set) {
    rawTags = firstArg;
    if (typeof secondArg === "number") cutoff = secondArg;
  } else if (firstArg && typeof firstArg === "object") {
    if (secondArg !== undefined && secondArg !== null && !(typeof secondArg === "number")) {
      rawTags = secondArg;
      if (typeof cutoffArg === "number") cutoff = cutoffArg;
    } else {
      rawTags = firstArg.opportunityTags || firstArg.tags || firstArg.opportunity_tags || [];
      if (typeof secondArg === "number") cutoff = secondArg;
    }
  } else {
    rawTags = secondArg !== undefined ? secondArg : [];
  }

  if (Array.isArray(rawTags)) {
    const freshStrings: string[] = [];
    for (const tagItem of rawTags) {
      if (typeof tagItem === "object" && tagItem !== null) {
        if (tagItem.status === "Expired Signal" || tagItem.status === "No Longer Observed" || tagItem.status === "expired") {
          continue;
        }
        const dateStr = tagItem.source_updated_at || tagItem.source_published_at || tagItem.source_posted_at;
        if (dateStr) {
          const { confidence } = calculateSignalDecay(dateStr);
          if (confidence < cutoff) continue;
        }
      }
      const normItem = normalizeTags(tagItem);
      freshStrings.push(...normItem);
    }
    return Array.from(new Set(freshStrings));
  }

  return normalizeTags(rawTags);
}

export function formatFreshnessAge(
  dateISO?: string | null,
  labelType: "checked" | "seen" | "posted" | "updated" | "published" = "checked"
): string {
  if (!dateISO || dateISO === "Unknown" || dateISO === "null" || dateISO === "undefined") {
    if (labelType === "posted" || labelType === "updated" || labelType === "published") {
      return "Date not specified by source";
    }
    return "Checked today";
  }

  const { daysOld, hasSourceDate } = calculateSignalDecay(dateISO);

  if (!hasSourceDate || daysOld === null) {
    if (labelType === "posted" || labelType === "updated" || labelType === "published") {
      return "Date not specified by source";
    }
    return "Checked today";
  }

  const prefix = labelType === "posted" ? "Posted"
    : labelType === "published" ? "Published"
    : labelType === "updated" ? "Updated"
    : labelType === "seen" ? "Found"
    : "Checked";

  if (daysOld === 0) return `${prefix} today`;
  if (daysOld === 1) return `${prefix} yesterday`;
  if (daysOld < 7) return `${prefix} ${daysOld} days ago`;
  if (daysOld < 30) return `${prefix} ${Math.floor(daysOld / 7)} weeks ago`;
  return `${prefix} ${Math.floor(daysOld / 30)} months ago`;
}


